import { useEffect } from 'react';
import { create } from 'zustand';

/**
 * 화면 제목의 **동적 슬롯 2개**. 라우트 handle.title 이 정적이라, 데이터로만 알 수 있는 이름
 * (발전소명 등)은 화면이 런타임에 채운다.
 *
 * 슬롯이 둘인 이유는 **소비처가 다르기 때문**이다:
 *   - `override` — 셸 헤더가 handle.title **대신** 표시. `document.title` 에도 반영.
 *   - `detail`   — `document.title` **전용**. 헤더는 무시한다.
 *
 * 합치면 안 되는 이유: 대시보드에서 발전소를 고르면 탭 제목엔 발전소명이 붙어야 하지만, 헤더는
 * "대시보드"로 남아야 어느 화면인지 알 수 있다.
 */
interface TitleSlots {
  override: string | null;
  detail: string | null;
}

const useTitleSlotsStore = create<TitleSlots>(() => ({
  override: null,
  detail: null,
}));

/** 현재 오버라이드 타이틀(없으면 null) — 셸 헤더(`useRouteTitle`)가 읽어 handle.title 대신 표시. */
export function useHeaderTitleOverride(): string | null {
  return useTitleSlotsStore((state) => state.override);
}

/** 두 슬롯의 현재 값 — **트리 밖**(런타임 배선)에서 쓰는 동기 접근자. */
export function getTitleSlots(): TitleSlots {
  return useTitleSlotsStore.getState();
}

/** 슬롯 변화 구독 — **트리 밖**(런타임 배선)용. `document.title` 동기화가 소비한다. */
export function subscribeTitleSlots(listener: () => void): () => void {
  return useTitleSlotsStore.subscribe((state, previous) => {
    if (state.override !== previous.override || state.detail !== previous.detail) listener();
  });
}

/**
 * 화면이 셸 헤더 타이틀을 **동적으로** 덮어쓴다 (예: 발전소 상세 = 발전소명). `document.title` 에도 반영.
 * falsy 를 넘기면 해제(= handle.title 사용).
 *
 * 언마운트 시 자동 해제하되 **내가 쓴 값이 아직 유효할 때만** 지운다(last-writer-wins). native 스택
 * 전환에서 다음 화면이 먼저 마운트돼 값을 쓰고, 이전 화면이 늦게 언마운트되며 그 값을 지우는 경합 방지.
 */
export function useSetHeaderTitle(title: string | null | undefined): void {
  const value = title ?? null;
  useEffect(() => {
    useTitleSlotsStore.setState({ override: value });
    return () => {
      if (useTitleSlotsStore.getState().override === value) {
        useTitleSlotsStore.setState({ override: null });
      }
    };
  }, [value]);
}

/**
 * `document.title` 끝에 붙일 이름 — **헤더는 바뀌지 않는다**.
 *
 * 대시보드·발전량 조회·인버터 상세분석·수익분석처럼 **화면 이름은 그대로고 보는 대상만 바뀌는** 경우에
 * 쓴다(`헤빛온 · 대시보드 · 태양광1호`). 해제 규칙은 [useSetHeaderTitle] 과 같다.
 */
export function useSetTitleDetail(detail: string | null | undefined): void {
  const value = detail ?? null;
  useEffect(() => {
    useTitleSlotsStore.setState({ detail: value });
    return () => {
      if (useTitleSlotsStore.getState().detail === value) {
        useTitleSlotsStore.setState({ detail: null });
      }
    };
  }, [value]);
}
